import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  styled,
  Divider,
  Collapse,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Business as ProjectsIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  ContactPhone as ContactIcon,
  Assignment as TasksIcon,
  CalendarMonth as CalendarIcon,
  ReceiptLong as ReceiptLongIcon,
  InsertChart as AnalyticsIcon,
  Call as CallsIcon,
  Home as SiteVisitsIcon,
  Assessment as ReportsIcon,
  ExpandLess,
  ExpandMore,
  Engineering as DeveloperIcon,
  Receipt as InvoiceIcon,
  Logout as LogoutIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  Add as AddIcon,
  List as ListIcon,
  Create as CreateIcon,
  TrendingUp as TrendingUpIcon,
  QuestionAnswer as QueryIcon,
  AccountBalance as FinanceIcon,
  Phone as PhoneIcon,
  Chat as WhatsAppIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';

// Responsive width values
const getDrawerWidth = (theme) => ({
  xs: '200px',
  sm: '210px',
  md: '220px',
  lg: '240px',
  xl: '260px',
});

// Further increase the collapsed width values
const getCollapsedWidth = (theme) => ({
  xs: '64px',    // Increased from 56px
  sm: '68px',    // Increased from 60px
  md: '72px',    // Increased from 64px
  lg: '76px',    // Increased from 68px
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
}));

const getMenuItems = (userRole) => {
  const items = [
    // {
    //   text: 'Dashboard',
    //   icon: <DashboardIcon />,
    //   path: '/dashboard',
    // },
    {
      text: 'Pending Accounts',
      icon: <AddIcon />,
      path: '/pending-accounts',
    },
    // {
    //   text: 'Manage Requests',
    //   icon: <AddIcon />,
    //   path: '/manage-requests',
    // },
    {
      text: 'Customers',
      icon: <PeopleIcon />,
      path: '/customers',
    },
    {
      text: 'Cloud Telephony', // New item
      icon: <PhoneIcon />,
      path: '/cloud-telephony',
    },
    {
      text: 'Queries',
      icon: <QueryIcon />,
      path: '/queries',
    },
    {
      text: 'Invoices',
      icon: <FinanceIcon />,
      path: '/finance/invoices'
    },
    {
      text: 'Plans',
      icon: <ReceiptLongIcon />,
      path: '/plans'
    },
    {
      text: 'WhatsApp',
      icon: <WhatsAppIcon />,
      path: '/whatsapp',
      subItems: [
        { text: 'Masters', path: '/whatsapp/masters' },
        { text: 'Customers WhatsApp Configs', path: '/whatsapp/customer-configs' },
      ],
    },
  ];

  return items;
};

// Define the exact colors from the image
const SELECTED_COLOR = 'rgb(37, 99, 235)'; // Bright blue
const SELECTED_BG = 'rgba(37, 99, 235, 0.04)'; // Very light blue background - adjusted opacity
const TEXT_COLOR = 'rgb(71, 84, 103)'; // Default text color

const Sidebar = ({ user, logout }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [expandedMenu, setExpandedMenu] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Add responsive detection
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Auto collapse on small screens or leads page
  useEffect(() => {
    if (isSmallScreen) {
      setOpen(false);
    }
    // Remove the auto-collapse for leads and calls pages
  }, [isSmallScreen]);

  // Auto-expand WhatsApp when on a WhatsApp sub-route
  useEffect(() => {
    if (location.pathname.startsWith('/whatsapp')) {
      setExpandedMenu('WhatsApp');
    }
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleSubMenuToggle = (menuName) => {
    setExpandedMenu(current => current === menuName ? '' : menuName);
  };

  const isSelected = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path);
  };

  // Helper function to check if item or its subitems are selected
  const isItemSelected = (item) => {
    if (isSelected(item.path)) return true;
    if (item.subItems) {
      return item.subItems.some(subItem => isSelected(subItem.path));
    }
    return false;
  };

  // Get menu items based on user role
  const menuItems = getMenuItems(user?.role);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: {
          xs: open ? getDrawerWidth(theme).xs : getCollapsedWidth(theme).xs,
          sm: open ? getDrawerWidth(theme).sm : getCollapsedWidth(theme).sm,
          md: open ? getDrawerWidth(theme).md : getCollapsedWidth(theme).md,
          lg: open ? getDrawerWidth(theme).lg : getCollapsedWidth(theme).lg,
          xl: open ? getDrawerWidth(theme).xl : getCollapsedWidth(theme).lg,
        },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: {
            xs: open ? getDrawerWidth(theme).xs : getCollapsedWidth(theme).xs,
            sm: open ? getDrawerWidth(theme).sm : getCollapsedWidth(theme).sm,
            md: open ? getDrawerWidth(theme).md : getCollapsedWidth(theme).md,
            lg: open ? getDrawerWidth(theme).lg : getCollapsedWidth(theme).lg,
            xl: open ? getDrawerWidth(theme).xl : getCollapsedWidth(theme).lg,
          },
          boxSizing: 'border-box',
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden',
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
          borderRadius: 0,
        },
      }}
    >
      <DrawerHeader
        sx={{
          height: '64px',  // Match exactly with Header height
          minHeight: '64px', // Force consistent height
          px: {
            xs: 1.5,
            sm: 1.75,
            md: 2,
          },
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxSizing: 'border-box', // Ensure border is included in height
          '& .MuiIconButton-root': {  // Target the IconButton specifically
            height: 32,              // Fixed height for the button
            width: 32,               // Fixed width for the button
          }
        }}
      >
        {open ? (
          <Typography 
            variant="subtitle1" 
            sx={{ 
              ml: { xs: 0.5, sm: 1 },
              fontSize: {
                xs: '0.85rem',
                sm: '0.9rem',
                md: '1rem',
              },
              fontWeight: 600,
              whiteSpace: 'nowrap',
              lineHeight: 1,  // Adjust line height
            }}
          >
            SalesGro Admin
          </Typography>
        ) : (
          <Typography 
            variant="subtitle2" 
            sx={{ 
              ml: { xs: 1, sm: 1.25 },
              fontWeight: 600,
              lineHeight: 1,  // Adjust line height
            }}
          >
            SA
          </Typography>
        )}
        <IconButton 
          onClick={handleDrawerToggle}
          size="small"
          sx={{ 
            p: { xs: 0.5, sm: 0.75 },
            mr: { xs: 1.5, sm: 2 },
          }}
        >
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </DrawerHeader>

      <List sx={{ 
        pt: { xs: 0.5, sm: 0.75 },  // Adjusted top padding
        pb: { xs: 0.25, sm: 0.5 },
      }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem
              button
              onClick={() => {
                if (item.subItems) {
                  handleSubMenuToggle(item.text);
                } else if (item.path) {
                  navigate(item.path);
                  setExpandedMenu('');
                }
              }}
              selected={isItemSelected(item)}
              sx={{
                minHeight: {
                  xs: 32,
                  sm: 36,
                  md: 40,
                },
                py: { xs: 0.25, sm: 0.5 },
                px: { xs: 1, sm: 1.25 },
                color: TEXT_COLOR,
                position: 'relative',
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  bgcolor: `${SELECTED_BG} !important`,
                  color: SELECTED_COLOR,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    bgcolor: SELECTED_COLOR,
                  },
                  '& .MuiListItemIcon-root': {
                    color: SELECTED_COLOR,
                  },
                  '& .MuiTypography-root': {
                    color: SELECTED_COLOR,
                    fontWeight: 500,
                  },
                },
                '&:hover': {
                  bgcolor: theme => isItemSelected(item) 
                    ? `${SELECTED_BG} !important`
                    : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: {
                    xs: 36,    // Increased from 32
                    sm: 40,    // Increased from 36
                    md: 44,    // Increased from 40
                  },
                  mr: open ? { xs: 1, sm: 1.5, md: 2 } : 'auto',
                  justifyContent: 'center',
                  color: isItemSelected(item) ? SELECTED_COLOR : TEXT_COLOR,
                  '& svg': {
                    fontSize: {
                      xs: '1.1rem',
                      sm: '1.2rem',
                      md: '1.25rem',
                    },
                  },
                }}
              >
                {item.icon}
              </ListItemIcon>
              {open && (
                <>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: {
                        xs: '0.8rem',
                        sm: '0.85rem',
                        md: '0.875rem',
                      },
                      fontWeight: 500,
                      color: isItemSelected(item) ? SELECTED_COLOR : TEXT_COLOR,
                    }}
                  />
                  {item.subItems && (
                    <Box sx={{ 
                      ml: 0.5,
                      color: isItemSelected(item) ? SELECTED_COLOR : TEXT_COLOR,
                    }}>
                      {expandedMenu === item.text ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  )}
                </>
              )}
            </ListItem>

            {item.subItems && open && (
              <Collapse in={expandedMenu === item.text} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItem
                      key={subItem.text}
                      button
                      onClick={() => navigate(subItem.path)}
                      selected={isSelected(subItem.path)}
                      sx={{
                        pl: { xs: 4, sm: 5, md: 6 },
                        py: { xs: 0.25, sm: 0.5 },
                        minHeight: {
                          xs: 28,
                          sm: 32,
                          md: 36,
                        },
                        position: 'relative',
                        color: TEXT_COLOR,
                        transition: 'all 0.2s ease',
                        '&.Mui-selected': {
                          bgcolor: `${SELECTED_BG} !important`,
                          color: SELECTED_COLOR,
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '2px',
                            bgcolor: SELECTED_COLOR,
                          },
                          '& .MuiTypography-root': {
                            color: SELECTED_COLOR,
                            fontWeight: 500,
                          },
                        },
                        '&:hover': {
                          bgcolor: theme => isSelected(subItem.path)
                            ? `${SELECTED_BG} !important`
                            : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={subItem.text}
                        primaryTypographyProps={{
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.8rem',
                            md: '0.85rem',
                          },
                          fontWeight: 500,
                          color: isSelected(subItem.path) ? SELECTED_COLOR : TEXT_COLOR,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
      {/* Logout button at the bottom */}
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        p: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
        zIndex: 2,
      }}>
        {open ? (
          <Button
            variant="clearBtnOutlined"
            color="primary"
            sx={{ minWidth: 120, fontWeight: 600, ml: 'auto' }}
            startIcon={<PowerSettingsNewIcon />}
            onClick={logout}
          >
            Logout
          </Button>
        ) : (
          <Tooltip title="Logout" placement="right">
            <IconButton onClick={logout} color="inherit">
              <PowerSettingsNewIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar; 